import { Nullable } from '@boss/types/b2b-b2c';

import type { campaign } from '../../types';
import bossApi from '../../utils/fetch-api';

const DEFAULT_OPTIONS = { m2m: true, authScope: process.env.BOSS_API_AUTH_SCOPE_PRICE };

/** Price API date-time parameter: today's UTC midnight by default, overridable. */
const priceDate = (now: Date = new Date()): string =>
  new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())).toISOString();

/**
 * Fetch campaigns for today's PriceDate.
 * Endpoint: GET /price/v2/campaign/campaigns?priceDate=<date-time>
 * 204/no-content (no active campaigns) → undefined, mirroring other m2m fetchers.
 */
// fallow-ignore-next-line
export const getCampaigns = async (
  locale: string,
  date: string = priceDate(),
): Promise<Nullable<campaign.ICampaign[]>> =>
  await bossApi<campaign.ICampaign[]>(
    `/price/v2/campaign/campaigns?${new URLSearchParams({ priceDate: date }).toString()}`,
    {
      ...DEFAULT_OPTIONS,
      locale,
    },
  );

/**
 * Fetch categories for one campaign and today's PriceDate.
 * Endpoint: GET /price/v2/campaign/categories?campaignId=<id>&priceDate=<date-time>
 * 204/no-content (no categories) → undefined, mirroring other m2m fetchers.
 */
// fallow-ignore-next-line
export const getCategories = async (
  locale: string,
  campaignId: string,
  date: string = priceDate(),
): Promise<Nullable<campaign.ICampaignCategory[]>> =>
  await bossApi<campaign.ICampaignCategory[]>(
    `/price/v2/campaign/categories?${new URLSearchParams({ campaignId, priceDate: date }).toString()}`,
    {
      ...DEFAULT_OPTIONS,
      locale,
    },
  );

/**
 * Fetch participating products for one campaign and one-or-more categories.
 * Endpoint: GET /price/v2/campaign/products?campaignId=<id>&category=<c1>&category=<c2>&priceDate=<date-time>
 * Response order is the display order — callers must preserve it as-is (ticket 45171).
 * 204/no-content (no products) → undefined, mirroring other m2m fetchers.
 *
 * ponytail: the upstream binder does NOT decode '+' as a space, so category values
 * are percent-encoded manually (%20) instead of URLSearchParams. Raw-query string
 * concatenation with encodeURIComponent.
 */
export const getProducts = async (
  locale: string,
  campaignId: string,
  categories: string[],
  date: string = priceDate(),
): Promise<Nullable<campaign.ICampaignProduct[]>> => {
  const query = [
    `campaignId=${encodeURIComponent(campaignId)}`,
    ...categories.map(category => `category=${encodeURIComponent(category)}`),
    `priceDate=${encodeURIComponent(date)}`,
  ].join('&');

  return await bossApi<campaign.ICampaignProduct[]>(`/price/v2/campaign/products?${query}`, {
    ...DEFAULT_OPTIONS,
    locale,
  });
};
